import React, { useContext, useEffect, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

// Paths that an incomplete-profile user is allowed to be on.
// Everything else redirects to /complete-profile?returnTo=<current>
const ALLOWED_PATHS = new Set([
  '/complete-profile',
  '/sign-in',
  '/sign-up',
  '/error',
]);

const isProfileComplete = (user) =>
  !!(user && user.name && user.email && user.mobileNumber && user.referralSource);

// Lightweight global toast — avoids pulling in a dep. The CompleteProfilePage
// also shows a static banner; this is the in-flight "hey, something happened"
// feedback the moment the user clicks a link.
const showBlockedToast = (() => {
  let lastFired = 0;
  return () => {
    const now = Date.now();
    if (now - lastFired < 2000) return; // throttle
    lastFired = now;
    const id = 'profile-gate-toast';
    document.getElementById(id)?.remove();
    const el = document.createElement('div');
    el.id = id;
    el.textContent = 'Please complete your profile to continue.';
    Object.assign(el.style, {
      position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
      background: '#1f2937', color: '#fff', padding: '12px 20px', borderRadius: '8px',
      fontSize: '14px', fontFamily: 'system-ui, sans-serif',
      boxShadow: '0 8px 20px rgba(0,0,0,0.25)', zIndex: 99999,
      opacity: '0', transition: 'opacity 0.2s ease',
    });
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 250);
    }, 2500);
  };
})();

const ProfileGate = ({ children }) => {
  const { user, authLoading } = useContext(AuthContext);
  const location = useLocation();

  const shouldBlock =
    !authLoading && user && !isProfileComplete(user) && !ALLOWED_PATHS.has(location.pathname);

  // Fire the toast as a side-effect when a navigation is blocked.
  const lastBlockedPathRef = useRef(null);
  useEffect(() => {
    if (shouldBlock && lastBlockedPathRef.current !== location.pathname) {
      lastBlockedPathRef.current = location.pathname;
      showBlockedToast();
    }
  }, [shouldBlock, location.pathname]);

  if (authLoading) return children;
  if (!user) return children;
  if (isProfileComplete(user)) return children;
  if (ALLOWED_PATHS.has(location.pathname)) return children;

  const returnTo = `${location.pathname}${location.search || ''}`;
  return <Navigate to={`/complete-profile?returnTo=${encodeURIComponent(returnTo)}&blocked=1`} replace />;
};

export default ProfileGate;
